import { heicTo } from "heic-to";

const HEIC_IMAGE_TYPES = ["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"];
const HEIC_EXTENSIONS = [".heic", ".heif"];
const AVIF_IMAGE_TYPES = ["image/avif"];
const AVIF_EXTENSIONS = [".avif"];

// Checks whether the upload flow can handle the selected browser File.
// Called by gauge_capture_controller.js before metadata extraction or preview.
// Example return value: true for JPEG, PNG, WebP, AVIF, HEIC, or HEIF uploads.
export function isSupportedUploadedImage(file) {
  if (file.type.startsWith("image/")) return true;

  return isHeicFile(file) || isAvifFile(file);
}

// Converts HEIC/HEIF files to browser-readable JPEG files and passes other images through.
// Called by gauge_capture_controller.js after metadata has been read from the original file.
// Example return value: File { name: "photo.jpg", type: "image/jpeg" }.
export async function prepareUploadedPhotoFile(file, fallbackBasename = "gauge-reading") {
  if (!isHeicFile(file)) return file;

  const jpegBlob = await heicTo({
    blob: file,
    type: "image/jpeg",
    quality: 0.9,
  });
  const filename = convertedHeicFilename(file.name, fallbackBasename);

  return new File([jpegBlob], filename, { type: "image/jpeg", lastModified: file.lastModified || Date.now() });
}

// Converts display names into safe lowercase filename prefixes.
// Called by gauge_capture_controller.js when naming captured or converted upload files.
// Example return value: "main-street-bridge".
export function fileSafeName(value, fallback = "gauge-reading") {
  return (
    (value || fallback)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || fallback
  );
}

// Reduces a canvas in place by the requested scale.
// Called by gauge_capture_controller.js if client-side photo resizing is enabled before upload.
// Example return value: undefined; canvas dimensions and pixels are mutated.
export function reduceCanvasSize(canvas, scale = 0.8) {
  const originalWidth = canvas.width;
  const originalHeight = canvas.height;
  const reducedWidth = Math.round(originalWidth * scale);
  const reducedHeight = Math.round(originalHeight * scale);

  const source = document.createElement("canvas");
  source.width = originalWidth;
  source.height = originalHeight;
  source.getContext("2d").drawImage(canvas, 0, 0);

  canvas.width = reducedWidth;
  canvas.height = reducedHeight;
  canvas.getContext("2d").drawImage(source, 0, 0, reducedWidth, reducedHeight);
}

// Detects HEIC/HEIF from MIME type or extension.
// Called by isSupportedUploadedImage() and prepareUploadedPhotoFile().
function isHeicFile(file) {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();

  return HEIC_IMAGE_TYPES.includes(type) || HEIC_EXTENSIONS.some((extension) => name.endsWith(extension));
}

// Detects AVIF from MIME type or extension.
// Called by isSupportedUploadedImage() when browsers provide an empty MIME type.
function isAvifFile(file) {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();

  return AVIF_IMAGE_TYPES.includes(type) || AVIF_EXTENSIONS.some((extension) => name.endsWith(extension));
}

// Produces a JPEG filename for a converted HEIC/HEIF upload.
// Called by prepareUploadedPhotoFile() after heic-to returns a JPEG blob.
function convertedHeicFilename(filename, fallbackBasename) {
  const basename = filename.replace(/\.[^.]+$/, "");

  return `${basename || fallbackBasename}.jpg`;
}
